import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Smile, Meh, Frown, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MOOD_BUTTONS = [
  { id: 'good',        label: 'Good',        Icon: Smile, color: 'text-emerald-700 border-emerald-200 bg-emerald-50 hover:bg-emerald-100' },
  { id: 'neutral',     label: 'OK',          Icon: Meh,   color: 'text-amber-700 border-amber-200 bg-amber-50 hover:bg-amber-100' },
  { id: 'concerning',  label: 'Concerning',  Icon: Frown, color: 'text-red-700 border-red-200 bg-red-50 hover:bg-red-100' },
];

/**
 * DailyCheckinWidget — Blueprint #34 enhancement.
 *
 * Drops into PatientCarePlan. Shows today's day number + a 3-tap mood
 * picker. Submits to /api/patient/{id}/care-plan/{plan_id}/check-in.
 * Surfaces the running completion count + today's submission.
 *
 * Props:
 *  - patientId, planId
 *  - planStartedAt (ISO) — used to compute "which day is today" relative to plan generation
 *  - getAuthHeader: () => {headers}
 */
export const DailyCheckinWidget = ({ patientId, planId, planStartedAt, getAuthHeader }) => {
  const [checkIns, setCheckIns] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [todayMood, setTodayMood] = useState(null);
  const [notes, setNotes] = useState('');

  // Today's day number (1-7) relative to plan generation.
  const planStart = planStartedAt ? new Date(planStartedAt) : new Date();
  const today = new Date();
  const dayNumber = Math.min(7, Math.max(1, Math.floor((today - planStart) / (1000 * 60 * 60 * 24)) + 1));

  const load = useCallback(async () => {
    if (!patientId || !planId) return;
    try {
      const { data } = await axios.get(`${API}/patient/${patientId}/care-plan/${planId}/check-ins`, getAuthHeader());
      setCheckIns(data.latest_by_day || []);
      const today = (data.latest_by_day || []).find((c) => c.day === dayNumber);
      if (today) setTodayMood(today.mood);
    } catch (e) { /* ignore */ }
  }, [patientId, planId, getAuthHeader, dayNumber]);

  useEffect(() => { load(); }, [load]);

  const submit = async (mood) => {
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/patient/${patientId}/care-plan/${planId}/check-in`,
        { day: dayNumber, mood, notes: notes || null },
        getAuthHeader(),
      );
      setTodayMood(mood);
      setNotes('');
      toast.success("Logged. Your care team will see this.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not submit');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="cn-card p-6 border-cn-primary/30 bg-cn-primary/[0.03] mb-5" data-testid="daily-checkin-widget">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
        <div>
          <div className="cn-eyebrow mb-1">Day {dayNumber} of 7 · Daily check-in</div>
          <h3 className="font-display font-bold text-xl text-cn-text dark:text-white">
            How did yesterday go?
          </h3>
          <p className="text-sm text-cn-text-muted mt-1 max-w-md">
            One tap. Your clinician sees this on their Today screen.
          </p>
        </div>
        <div className="text-right text-xs text-cn-text-muted" data-testid="checkin-progress">
          <strong className="text-cn-text">{checkIns.length}</strong> of 7 days logged
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 mb-3" data-testid="checkin-buttons">
        {MOOD_BUTTONS.map(({ id, label, Icon, color }) => {
          const selected = todayMood === id;
          return (
            <button
              key={id}
              type="button"
              onClick={() => submit(id)}
              disabled={submitting}
              className={`px-3 py-3 rounded-xl border-2 font-semibold text-sm transition-all flex flex-col items-center gap-1 ${color} ${selected ? 'ring-2 ring-offset-2 ring-cn-primary' : ''}`}
              data-testid={`checkin-${id}`}
            >
              <Icon className="w-5 h-5" />
              {label}
            </button>
          );
        })}
      </div>

      <input
        type="text"
        className="cn-input w-full text-sm"
        placeholder="Optional note (e.g. 'took meds, walked 10 min')"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        maxLength={300}
        data-testid="checkin-notes"
      />

      {todayMood && (
        <p className="text-xs text-cn-primary mt-3 inline-flex items-center gap-1.5" data-testid="checkin-confirmed">
          <RefreshCw className="w-3 h-3" />
          Today is logged as <strong>{todayMood}</strong>. Tap a different button to update.
        </p>
      )}
    </section>
  );
};

export default DailyCheckinWidget;
